// SPDX-License-Identifier: AGPL-3.0-or-later

import kitStyles from '@app/features/app/components/dialogs/shared/SettingsKit.module.css';
import {ChoiceCard} from '@app/features/user/components/modals/tabs/appearance_tab/cards/ChoiceCard';
import {clsx} from 'clsx';
import type React from 'react';
import {useRef} from 'react';

export interface SettingsChoiceCardOption<T extends string | number> {
	value: T;
	name: string;
	desc?: string;
	disabled?: boolean;
	/** A picture of the choice above its name, as on the Appearance theme cards. */
	preview?: React.ReactNode;
}

interface SettingsChoiceCardsProps<T extends string | number> {
	options: ReadonlyArray<SettingsChoiceCardOption<T>>;
	value: T;
	onChange: (value: T) => void;
	'aria-label': string;
	disabled?: boolean;
	className?: string;
	'data-flx'?: string;
}

/**
 * KONVO round 8: a one-of-N choice as a row of big cards (radio, name, one line), for choices
 * that deserve more weight than radio rows (voice processing, assistant proactivity).
 * Arrow keys move the choice like a radio group.
 */
export function SettingsChoiceCards<T extends string | number>({
	options,
	value,
	onChange,
	'aria-label': ariaLabel,
	disabled,
	className,
	'data-flx': dataFlx,
}: SettingsChoiceCardsProps<T>): React.ReactElement {
	const refs = useRef<Array<HTMLButtonElement | null>>([]);
	const enabled = options.filter((option) => !option.disabled && !disabled);
	const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, current: T) => {
		const step =
			event.key === 'ArrowRight' || event.key === 'ArrowDown'
				? 1
				: event.key === 'ArrowLeft' || event.key === 'ArrowUp'
					? -1
					: 0;
		if (step === 0 || enabled.length === 0) return;
		event.preventDefault();
		const at = enabled.findIndex((option) => option.value === current);
		const next = enabled[(at + step + enabled.length) % enabled.length];
		onChange(next.value);
		refs.current[options.indexOf(next)]?.focus();
	};
	return (
		<div
			role="radiogroup"
			aria-label={ariaLabel}
			className={clsx(
				kitStyles.choiceGrid,
				options.some((option) => option.preview) && kitStyles.choiceGridPreview,
				className,
			)}
			data-flx={dataFlx}
		>
			{options.map((option, index) => (
				<ChoiceCard
					key={String(option.value)}
					ref={(node) => {
						refs.current[index] = node;
					}}
					name={option.name}
					description={option.desc}
					checked={option.value === value}
					disabled={disabled || option.disabled}
					preview={option.preview ?? null}
					onSelect={() => onChange(option.value)}
					onKeyDown={(event) => handleKeyDown(event, option.value)}
					data-flx={dataFlx ? `${dataFlx}.${String(option.value)}` : undefined}
				/>
			))}
		</div>
	);
}
